import { kioskModeActive } from '@classes/app_url_params'
import { getThemeName } from '@config'
import useApi from '@hooks/useApi'
import type { ThemeOptions } from '@mui/material'
import { GlobalStyles } from '@mui/material'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { defaultConfig } from '@shared_types/theme_tokens/configs/default'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { getTheme } from '../theme'
import { ThemeContext } from './ThemeContext'
import { getCSSVariables } from './utils'

export function ThemeProvider ({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const isKiosk = kioskModeActive()
  const isAdmin = location.pathname.startsWith('/admin')
  const isGallery = window.location.href.includes('/gallery')
  const { apiGet } = useApi()

  const [themeConfigs, setThemeConfigs] = useState<Record<string, ThemeOptions['tokens']>>({})

  const [themeId, setThemeId] = useState<string>(getThemeName('default'))

  const theme = useMemo(
    () => getTheme(isKiosk, isAdmin, isGallery, { ...defaultConfig, ...themeConfigs[themeId] }),
    [isKiosk, isAdmin, isGallery, themeConfigs, themeId]
  )

  useEffect(() => {
    if (themeConfigs[themeId]) {
      return
    }
    apiGet<ThemeOptions['tokens']>(`/api/theme/${themeId}`).then(config => {
      setThemeConfigs(themeConfigs => ({
        ...themeConfigs,
        [themeId]: config
      }))
    })
  }, [themeId])

  useEffect(() => {
    document.documentElement.classList.toggle('is-admin', isAdmin)
    return () => {
      document.documentElement.classList.remove('is-admin')
    }
  }, [isAdmin])

  return (
    <ThemeContext.Provider
      value={{ themeId, setThemeId }}
    >
      <MuiThemeProvider theme={theme}>
        <GlobalStyles
          styles={theme => ({
            ':root': getCSSVariables(theme.tokens)
          })}
        />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
